import { useRef, useState } from "react";
import {
  Upload, X, Loader2, FileText, AlertTriangle, Check, Trash2, Edit3, Sparkles,
} from "lucide-react";
import api, { API } from "../lib/api";
import Modal from "./Modal";
import { toast } from "./ui/sonner";
import { useBranding } from "../context/TenantBrandingContext";

/**
 * BulkImportModal — Wave 25.10
 * Shared between Inventory and Suppliers.  Drop a CSV / XLSX / PDF / image,
 * Hidro extracts rows, the user reviews each row (with duplicate warnings),
 * picks per-row action (Create / Merge / Skip), then commits.
 *
 * Props:
 *   kind: "inventory" | "suppliers"
 *   onClose
 *   onCommitted({created, merged, skipped})
 */
export default function BulkImportModal({ kind = "inventory", onClose, onCommitted }) {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [phase, setPhase] = useState("pick"); // pick | extracting | review | committing
  const [filename, setFilename] = useState("");
  const [engine, setEngine] = useState("");
  const [rows, setRows] = useState([]);
  const [committing, setCommitting] = useState(false);
  const fileRef = useRef(null);

  const isSuppliers = kind === "suppliers";
  const endpoint = isSuppliers ? "/suppliers/import" : "/inventory/import";
  const title = isSuppliers ? "Import suppliers" : "Import inventory items";

  const fields = isSuppliers
    ? ["name", "email", "phone", "physical_address", "external_ref", "notes"]
    : ["name", "sku", "manufacturer", "category", "unit", "default_quantity", "description"];

  const handleFile = async (file) => {
    if (!file) return;
    setPhase("extracting");
    setFilename(file.name);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("hv_token");
      const resp = await fetch(`${API}${endpoint}/extract`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const initial = (data.rows || []).map((r) => ({
        ...r,
        // Default action: skip if duplicate, create otherwise
        action: r._dup_match ? "skip" : "create",
        merge_target_id: r._dup_match?.id || null,
      }));
      setRows(initial);
      setEngine(data.engine || "");
      setPhase("review");
      if (initial.length === 0) {
        toast.warning(`${aiName} couldn't find any rows in that file.`);
      } else {
        toast.success(`Found ${initial.length} row${initial.length === 1 ? "" : "s"}.`);
      }
    } catch (err) {
      toast.error(err?.message || "Extract failed.");
      setPhase("pick");
    }
  };

  const setRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };
  const setAllAction = (action) => {
    setRows((prev) => prev.map((r) => ({ ...r, action })));
  };

  const handleCommit = async () => {
    const toSubmit = rows.filter((r) => r.action !== "skip");
    if (toSubmit.length === 0) {
      toast.warning("No rows selected to import.");
      return;
    }
    setCommitting(true);
    setPhase("committing");
    try {
      const { data } = await api.post(`${endpoint}/commit`, { rows });
      toast.success(`Created ${data.created}, merged ${data.merged}, skipped ${data.skipped}.`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} rows had errors — check the report.`);
      }
      onCommitted?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed.");
      setPhase("review");
    } finally {
      setCommitting(false);
    }
  };

  const dupCount = rows.filter((r) => r._dup_match).length;
  const createCount = rows.filter((r) => r.action === "create").length;
  const mergeCount = rows.filter((r) => r.action === "merge").length;
  const skipCount = rows.filter((r) => r.action === "skip").length;

  return (
    <Modal open onClose={onClose} title={title} testId="bulk-import-modal" maxWidth="max-w-6xl">
      {phase === "pick" && (
        <div
          className="border-2 border-dashed border-white/15 hover:border-[#1FB6C1]/50 rounded-md p-12 text-center cursor-pointer transition"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer?.files?.[0];
            if (f) handleFile(f);
          }}
          data-testid="bulk-dropzone"
        >
          <Upload className="w-10 h-10 mx-auto text-[#1FB6C1] mb-3" />
          <div className="font-display text-lg mb-1">Drop a file here</div>
          <div className="text-xs text-[#a0aabf] mb-3">
            CSV / XLSX (auto-mapped from headers) · PDF ({aiName} reads it) · image ({aiName} vision)
          </div>
          <button className="hv-btn hv-btn-primary" data-testid="bulk-pick-btn">
            <FileText className="w-4 h-4" /> Choose file
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls,application/pdf,image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="bulk-file-input"
          />
          <div className="text-[10px] text-[#647087] mt-3">
            {aiName} will preview every row + flag duplicates before anything is saved.
          </div>
        </div>
      )}

      {phase === "extracting" && (
        <div className="p-12 text-center">
          <Loader2 className="w-10 h-10 mx-auto text-[#1FB6C1] animate-spin mb-3" />
          <div className="font-display text-lg mb-1">Reading {filename}…</div>
          <div className="text-xs text-[#a0aabf]">
            <Sparkles className="w-3 h-3 inline mr-1 text-[#1FB6C1]" />
            {aiName} is parsing rows and checking for duplicates.
          </div>
        </div>
      )}

      {(phase === "review" || phase === "committing") && (
        <div className="space-y-3" data-testid="bulk-review">
          {/* Summary bar */}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="text-[#a0aabf]">
              <Sparkles className="w-3 h-3 inline mr-1 text-[#1FB6C1]" />
              <strong className="text-white">{rows.length}</strong> rows from{" "}
              <span className="font-mono text-[#cbd2df]">{filename}</span>
              {engine && <span className="text-[#647087]"> · engine: {engine}</span>}
            </span>
            {dupCount > 0 && (
              <span className="text-[#F59E0B]">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                {dupCount} possible duplicate{dupCount === 1 ? "" : "s"}
              </span>
            )}
            <div className="ml-auto flex gap-1">
              <button onClick={() => setAllAction("create")} className="hv-btn hv-btn-ghost text-[10px]" data-testid="bulk-all-create">All Create</button>
              <button onClick={() => setAllAction("merge")} className="hv-btn hv-btn-ghost text-[10px]" data-testid="bulk-all-merge">All Merge</button>
              <button onClick={() => setAllAction("skip")} className="hv-btn hv-btn-ghost text-[10px]" data-testid="bulk-all-skip">All Skip</button>
            </div>
          </div>
          <div className="text-[11px] text-[#647087] flex flex-wrap gap-3">
            <span><Check className="w-3 h-3 inline text-emerald-300" /> Create: {createCount}</span>
            <span><Edit3 className="w-3 h-3 inline text-[#1FB6C1]" /> Merge: {mergeCount}</span>
            <span><X className="w-3 h-3 inline text-[#647087]" /> Skip: {skipCount}</span>
          </div>

          {/* Rows table */}
          <div className="overflow-x-auto border border-white/10 rounded-md max-h-[55vh] overflow-y-auto" data-testid="bulk-rows-table">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.03] sticky top-0 z-10">
                <tr>
                  <th className="text-left p-2 w-32">Action</th>
                  {fields.map((f) => (
                    <th key={f} className="text-left p-2 uppercase tracking-wider text-[10px] text-[#a0aabf]">
                      {f.replace(/_/g, " ")}
                    </th>
                  ))}
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    className={`border-t border-white/5 ${r._dup_match ? "bg-amber-500/5" : ""}`}
                    data-testid={`bulk-row-${i}`}
                  >
                    <td className="p-2 align-top">
                      <select
                        value={r.action}
                        onChange={(e) => setRow(i, { action: e.target.value })}
                        className="hv-input text-[11px] py-1 px-1.5 w-full"
                        data-testid={`bulk-row-action-${i}`}
                      >
                        <option value="create">Create</option>
                        {r._dup_match && <option value="merge">Merge → {r._dup_match.name?.slice(0, 24)}</option>}
                        <option value="skip">Skip</option>
                      </select>
                      {r._dup_match && (
                        <div className="text-[9px] text-[#F59E0B] mt-1 leading-tight">
                          ⚠ {r._dup_match.reason}
                        </div>
                      )}
                    </td>
                    {fields.map((f) => (
                      <td key={f} className="p-1 align-top">
                        <input
                          value={r[f] ?? ""}
                          onChange={(e) => setRow(i, { [f]: e.target.value })}
                          className="hv-input text-[11px] py-1 px-1.5 w-full"
                          data-testid={`bulk-row-${i}-${f}`}
                        />
                      </td>
                    ))}
                    <td className="p-1 align-top">
                      <button
                        onClick={() => removeRow(i)}
                        className="text-[#647087] hover:text-rose-300"
                        title="Remove row"
                        data-testid={`bulk-row-remove-${i}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={fields.length + 2} className="p-6 text-center text-[#647087]">No rows.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
            <button
              type="button"
              onClick={onClose}
              disabled={committing}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="bulk-cancel"
            >Cancel</button>
            <button
              type="button"
              onClick={handleCommit}
              disabled={committing || (createCount + mergeCount === 0)}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="bulk-commit"
            >
              {committing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Import {createCount + mergeCount} row{(createCount + mergeCount) === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
